import { PageController } from '@client/components/_core/layout/PageController';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import {
    CertaintyTag,
    ControlFrequencyTag,
    ImpactTag,
    IncidentFrequencyTag,
    RatingTag,
    SeverityTag,
    SourceTag,
} from '@client/components/_ui/display/tags';
import { Space } from '@client/components/_ui/layout/Space';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Tag, Typography } from 'antd';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/tag-variants')({
    staticData: {
        breadcrumb: { title: 'Tags & Badges' },
        featureFlag: 'enable-debug-info',
    },
    component: TagVariantsShowcase,
});

function TagVariantsShowcase() {
    return (
        <PageController route={Route}>
            <div className="flex flex-col gap-6 p-6">
                <div>
                    <Title level={2}>Tags &amp; Badges</Title>
                    <Paragraph className="text-muted-foreground">
                        This page showcases <strong>Tags</strong> (categorisation/classification) and{' '}
                        <strong>Badges</strong> (workflow status). Understanding when to use each is critical for
                        consistent UI patterns.
                    </Paragraph>
                </div>

                {/* Tags vs Badges explanation */}
                <Card title="Tags vs Badges" size="small" className="border-amber-200 bg-amber-50">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <Paragraph className="mb-2 font-semibold text-muted-foreground">
                                Tags - &quot;What is this?&quot;
                            </Paragraph>
                            <ul className="list-inside list-disc text-muted-foreground text-sm">
                                <li>Categorisation, classification, attributes</li>
                                <li>An item can have multiple tags</li>
                                <li>User-assigned or categorical</li>
                                <li>Examples: severity, certainty, source</li>
                            </ul>
                        </div>
                        <div>
                            <Paragraph className="mb-2 font-semibold text-muted-foreground">
                                Badges - &quot;Where in the process?&quot;
                            </Paragraph>
                            <ul className="list-inside list-disc text-muted-foreground text-sm">
                                <li>Workflow state, process status</li>
                                <li>Mutually exclusive (one status at a time)</li>
                                <li>System-controlled, changes over time</li>
                                <li>Examples: Draft, Pending, Approved</li>
                            </ul>
                        </div>
                    </div>
                </Card>

                <Divider titlePlacement="start">
                    <Title level={3} className="m-0">
                        Badge Components (StatusBadge)
                    </Title>
                </Divider>

                {/* StatusBadge Variants */}
                <Card title="StatusBadge Variants" size="small">
                    <Paragraph className="text-muted-foreground">
                        StatusBadge supports three variants: <code>dot</code> (indicator only), <code>filled</code>{' '}
                        (pill badge for headers), <code>outlined</code> (Ant Design Tag for tables/lists).
                    </Paragraph>
                    <div className="flex flex-col gap-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Dot variant (for compact display):
                            </Text>
                            <Space size="small">
                                <StatusBadge status="open" module="incident" variant="dot" />
                                <StatusBadge status="closed" module="incident" variant="dot" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Filled variant (for headers):
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="open" module="incident" variant="filled" />
                                <StatusBadge status="closed" module="incident" variant="filled" />
                                {/* <StatusBadge status="pending" module="claim" variant="filled" /> */}
                                <StatusBadge status="approved" module="userAttestation" variant="filled" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Outlined variant (default, for tables):
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="open" module="incident" variant="outlined" />
                                <StatusBadge status="closed" module="incident" variant="outlined" />
                                {/* <StatusBadge status="pending" module="claim" variant="outlined" /> */}
                                <StatusBadge status="approved" module="userAttestation" variant="outlined" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<StatusBadge status="open" module="incident" variant="filled" />'}</code>
                    </Text>
                </Card>

                {/* StatusBadge Modules */}
                <Card title="StatusBadge by Module" size="small">
                    <Paragraph className="text-muted-foreground">
                        StatusBadge uses module-specific status configurations. Each module has its own set of valid
                        statuses.
                    </Paragraph>
                    <div className="flex flex-col gap-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Incident statuses:
                            </Text>
                            <Space size="small">
                                <StatusBadge status="open" module="incident" />
                                <StatusBadge status="closed" module="incident" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Complaint statuses:
                            </Text>
                            <Space size="small">
                                <StatusBadge status="OPEN" module="complaint" />
                                <StatusBadge status="CLOSED" module="complaint" />
                                <StatusBadge status="RE_OPENED" module="complaint" />
                            </Space>
                        </div>
                        {/* <div>
                            <Text strong className="mb-2 block">
                                Claim statuses:
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="pending" module="claim" />
                                <StatusBadge status="open" module="claim" />
                                <StatusBadge status="closed" module="claim" />
                                <StatusBadge status="withdrawn" module="claim" />
                                <StatusBadge status="cancelled" module="claim" />
                                <StatusBadge status="reopened" module="claim" />
                            </Space>
                        </div> */}
                        <div>
                            <Text strong className="mb-2 block">
                                Obligation statuses:
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="DRAFT" module="obligation" />
                                <StatusBadge status="AWAITING_REVIEW" module="obligation" />
                                <StatusBadge status="PUBLISHED" module="obligation" />
                                <StatusBadge status="DECOMMISSIONED" module="obligation" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Task statuses:
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="NOT_STARTED" module="task" />
                                <StatusBadge status="IN_PROGRESS" module="task" />
                                <StatusBadge status="DONE" module="task" />
                                <StatusBadge status="BLOCKED" module="task" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                User Attestation statuses:
                            </Text>
                            <Space size="small" wrap>
                                <StatusBadge status="not_started" module="userAttestation" />
                                <StatusBadge status="in_progress" module="userAttestation" />
                                <StatusBadge status="submitted" module="userAttestation" />
                                <StatusBadge status="approved" module="userAttestation" />
                                <StatusBadge status="rejected" module="userAttestation" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Available modules: incident, complaint, claim, obligation, fsg, task, documentAnalysis,
                        emailStatus, messageClassification, userAttestation, formSchema, pdsProcessing
                    </Text>
                </Card>

                <Divider titlePlacement="start">
                    <Title level={3} className="m-0">
                        Tag Components
                    </Title>
                </Divider>

                {/* Tag Variants Comparison */}
                <Card title="Tag variants" size="small">
                    <Paragraph className="text-muted-foreground">
                        Domain tags support two variants: <code>outlined</code> (default, uses Ant Design Tag) and{' '}
                        <code>filled</code> (custom implementation with better contrast).
                    </Paragraph>
                    <div className="flex flex-col gap-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Outlined (default):
                            </Text>
                            <Space size="small" wrap>
                                <SeverityTag value="high" />
                                <SeverityTag value="medium" />
                                <SeverityTag value="low" />
                                <CertaintyTag value="HIGH" />
                                <SourceTag value="internal" />
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Filled (better contrast):
                            </Text>
                            <Space size="small" wrap>
                                <SeverityTag value="high" variant="filled" />
                                <SeverityTag value="medium" variant="filled" />
                                <SeverityTag value="low" variant="filled" />
                                <CertaintyTag value="HIGH" variant="filled" />
                                <SourceTag value="internal" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<SeverityTag value="high" variant="filled" />'}</code>
                    </Text>
                </Card>

                {/* Ant Design Tag (raw) */}
                <Card title="Ant Design Tag (raw)" size="small">
                    <Paragraph className="text-muted-foreground">
                        The base Ant Design Tag component - note the poor contrast on the filled variant with light
                        backgrounds. Use domain-specific tags with <code>variant=&quot;filled&quot;</code> instead.
                    </Paragraph>
                    <div className="flex flex-col gap-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Ant Design outlined:
                            </Text>
                            <Space size="small">
                                <Tag variant="outlined" color="success">
                                    Success
                                </Tag>
                                <Tag variant="outlined" color="warning">
                                    Warning
                                </Tag>
                                <Tag variant="outlined" color="error">
                                    Error
                                </Tag>
                            </Space>
                        </div>
                        <div>
                            <Text strong className="mb-2 block">
                                Ant Design filled (poor contrast):
                            </Text>
                            <Space size="small">
                                <Tag variant="filled" color="success">
                                    Success
                                </Tag>
                                <Tag variant="filled" color="warning">
                                    Warning
                                </Tag>
                                <Tag variant="filled" color="error">
                                    Error
                                </Tag>
                            </Space>
                        </div>
                    </div>
                </Card>

                <Divider titlePlacement="start">
                    <Title level={4} className="m-0">
                        Domain-Specific Tags
                    </Title>
                </Divider>

                <Card title="SeverityTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Severity colour scheme: LOW = green (minimal concern), MEDIUM = orange (moderate concern), HIGH
                        = red (significant concern).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <SeverityTag value="low" />
                                <SeverityTag value="medium" />
                                <SeverityTag value="high" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <SeverityTag value="low" variant="filled" />
                                <SeverityTag value="medium" variant="filled" />
                                <SeverityTag value="high" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<SeverityTag value="high" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="CertaintyTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Certainty colour scheme (inverse of severity): LOW = gray (uncertain), MEDIUM = orange (somewhat
                        confident), HIGH = green (highly confident).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <CertaintyTag value="LOW" />
                                <CertaintyTag value="MEDIUM" />
                                <CertaintyTag value="HIGH" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <CertaintyTag value="LOW" variant="filled" />
                                <CertaintyTag value="MEDIUM" variant="filled" />
                                <CertaintyTag value="HIGH" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<CertaintyTag value="HIGH" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="ImpactTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Impact colour scheme (same as Severity): LOW = green (minimal concern), MEDIUM = orange
                        (moderate concern), HIGH = red (significant concern).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <ImpactTag value="low" />
                                <ImpactTag value="medium" />
                                <ImpactTag value="high" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <ImpactTag value="low" variant="filled" />
                                <ImpactTag value="medium" variant="filled" />
                                <ImpactTag value="high" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<ImpactTag value="high" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="IncidentFrequencyTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Incident Frequency colour scheme (same as Severity): LOW = green (rare occurrences), MEDIUM =
                        orange (occasional occurrences), HIGH = red (frequent occurrences).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <IncidentFrequencyTag value="low" />
                                <IncidentFrequencyTag value="medium" />
                                <IncidentFrequencyTag value="high" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <IncidentFrequencyTag value="low" variant="filled" />
                                <IncidentFrequencyTag value="medium" variant="filled" />
                                <IncidentFrequencyTag value="high" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<IncidentFrequencyTag value="high" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="RatingTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Control Activity Rating colour scheme: EFFECTIVE = green (working well), NEEDS_IMPROVEMENT =
                        orange (requires attention), NOT_EFFECTIVE = red (failing).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <RatingTag value="EFFECTIVE" />
                                <RatingTag value="NEEDS_IMPROVEMENT" />
                                <RatingTag value="NOT_EFFECTIVE" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <RatingTag value="EFFECTIVE" variant="filled" />
                                <RatingTag value="NEEDS_IMPROVEMENT" variant="filled" />
                                <RatingTag value="NOT_EFFECTIVE" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<RatingTag value="EFFECTIVE" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="ControlFrequencyTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Control Frequency colour scheme - gradient from most frequent (green) to least frequent
                        (gray/default). Ordered by frequency.
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Outlined:
                            </Text>
                            <Space size="small" wrap>
                                <ControlFrequencyTag value="MULTIPLE_TIMES_A_DAY" />
                                <ControlFrequencyTag value="DAILY" />
                                <ControlFrequencyTag value="WEEKLY" />
                                <ControlFrequencyTag value="FORTNIGHTLY" />
                                <ControlFrequencyTag value="MONTHLY" />
                                <ControlFrequencyTag value="EVERY_3_MONTHS" />
                                <ControlFrequencyTag value="EVERY_6_MONTHS" />
                                <ControlFrequencyTag value="ANNUAL" />
                                <ControlFrequencyTag value="AD_HOC" />
                            </Space>
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Filled:
                            </Text>
                            <Space size="small" wrap>
                                <ControlFrequencyTag value="MULTIPLE_TIMES_A_DAY" variant="filled" />
                                <ControlFrequencyTag value="DAILY" variant="filled" />
                                <ControlFrequencyTag value="WEEKLY" variant="filled" />
                                <ControlFrequencyTag value="FORTNIGHTLY" variant="filled" />
                                <ControlFrequencyTag value="MONTHLY" variant="filled" />
                                <ControlFrequencyTag value="EVERY_3_MONTHS" variant="filled" />
                                <ControlFrequencyTag value="EVERY_6_MONTHS" variant="filled" />
                                <ControlFrequencyTag value="ANNUAL" variant="filled" />
                                <ControlFrequencyTag value="AD_HOC" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<ControlFrequencyTag value="DAILY" variant="filled" />'}</code>
                    </Text>
                </Card>

                <Card title="SourceTag" size="small">
                    <Paragraph className="text-muted-foreground">
                        Source colour scheme: INTERNAL = cyan (corporate/internal feel), EXTERNAL = purple (distinct
                        visual separation).
                    </Paragraph>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Outlined:
                            </Text>
                            <Space size="small">
                                <SourceTag value="internal" />
                                <SourceTag value="external" />
                            </Space>
                        </div>
                        <div className="flex items-center gap-2">
                            <Text type="secondary" className="w-16 text-xs">
                                Filled:
                            </Text>
                            <Space size="small">
                                <SourceTag value="internal" variant="filled" />
                                <SourceTag value="external" variant="filled" />
                            </Space>
                        </div>
                    </div>
                    <Divider />
                    <Text type="secondary" className="text-xs">
                        Usage: <code>{'<SourceTag value="internal" variant="filled" />'}</code>
                    </Text>
                </Card>
            </div>
        </PageController>
    );
}
